(function () {
  const supportsScrollEnd = 'onscrollend' in window;
  const initializedSections = new WeakMap();

  function initSection(section) {
    if (initializedSections.has(section)) return;

    const dataScript = section.querySelector('.js-features-data');
    if (!dataScript) return;

    const featuresData = JSON.parse(dataScript.textContent);
    if (featuresData.length === 0) return;

    featuresData.forEach(data => {
      if (data.image) {
        const imgDesktop = new Image();
        imgDesktop.src = data.image;
      }
      if (data.mobileImage) {
        const imgMobile = new Image();
        imgMobile.src = data.mobileImage;
      }
    });

    const track = section.querySelector('.js-track');
    const floatingBox = section.querySelector('.js-floating-box');
    const imageDesktop = section.querySelector('.js-image-desktop');
    const imageMobile = section.querySelector('.js-image-mobile');
    const imageContainer = section.querySelector('.js-image-container');
    const svgConnector = section.querySelector('.js-connector');
    const svgLine = section.querySelector('.js-connector-line');
    const svgDot = section.querySelector('.js-connector-dot');
    const trackerItems = section.querySelectorAll('.js-tracker-item');
    const glowElement = section.querySelector('.js-glow');

    const elTitle = section.querySelector('.js-title');
    const elDesc = section.querySelector('.js-desc');
    const elSpecs = section.querySelector('.js-specs');

    let activeIndex = -1;
    let isTicking = false;
    let transitionTimeout = null;
    let scrollSnapTimeout = null;
    let snapSafetyTimeout = null;
    let resizeTimeout = null;
    let lastScrollPos = window.scrollY || document.documentElement.scrollTop;
    let scrollDirection = 1;
    let isSnapping = false;

    function updateContent(index) {
      if (activeIndex === index) return;

      const isInitialLoad = activeIndex === -1;
      activeIndex = index;
      const data = featuresData[index];
      if (!data) return;

      if (glowElement && data.glowColor) {
        glowElement.style.setProperty('--glow-color', data.glowColor);
      }

      trackerItems.forEach((item, i) => {
        if (i === index) item.classList.add('scrolling-features__tracker-item--active');
        else item.classList.remove('scrolling-features__tracker-item--active');
      });

      if (!isInitialLoad) floatingBox.style.opacity = 0;

      const needsDesktopSwap = imageDesktop && !imageDesktop.src.includes(data.image) && data.image !== '';
      const needsMobileSwap = imageMobile && !imageMobile.src.includes(data.mobileImage) && data.mobileImage !== '';

      if (!isInitialLoad) {
        if (needsDesktopSwap) imageDesktop.style.opacity = 0;
        if (needsMobileSwap) imageMobile.style.opacity = 0;
      }

      const performDomUpdate = () => {
        elTitle.innerHTML = data.title;
        elDesc.textContent = data.desc;

        if (elSpecs) {
          let specsHTML = '';
          data.specs.forEach(spec => {
            if (spec.val || spec.lbl) {
              const layoutClass = spec.layout === 'Full Width' ? 'scrolling-features__spec-item--full' : 'scrolling-features__spec-item--half';
              specsHTML += `
                <div class="scrolling-features__spec-item ${layoutClass}">
                  ${spec.icon ? `<img class="scrolling-features__spec-icon" src="${spec.icon}" alt="" loading="lazy" />` : ''}
                  ${spec.val ? `<span class="scrolling-features__spec-val">${spec.val}</span>` : ''}
                  ${spec.lbl ? `<span class="scrolling-features__spec-lbl">${spec.lbl}</span>` : ''}
                </div>
              `;
            }
          });
          elSpecs.innerHTML = specsHTML;
        }

        if (needsDesktopSwap) {
          imageDesktop.onload = () => { imageDesktop.style.opacity = 1; };
          imageDesktop.removeAttribute('srcset');
          imageDesktop.src = data.image;
          if (data.alt !== undefined) imageDesktop.alt = data.alt;
          if (imageDesktop.complete) imageDesktop.style.opacity = 1;
        }

        if (needsMobileSwap) {
          imageMobile.onload = () => { imageMobile.style.opacity = 1; };
          imageMobile.removeAttribute('srcset');
          imageMobile.src = data.mobileImage;
          if (data.alt !== undefined) imageMobile.alt = data.alt;
          if (imageMobile.complete) imageMobile.style.opacity = 1;
        }

        floatingBox.style.opacity = 1;
      };

      if (isInitialLoad) performDomUpdate();
      else {
        if (transitionTimeout) clearTimeout(transitionTimeout);
        transitionTimeout = setTimeout(performDomUpdate, 300);
      }
    }

    // --- STEP 2: Calibrate SVG dimensions to match window coordinate space ---
    function updateSvgDimensions() {
      if (!svgConnector) return;
      const width = window.innerWidth;
      const height = window.innerHeight;
      svgConnector.setAttribute('width', width);
      svgConnector.setAttribute('height', height);
      svgConnector.setAttribute('viewBox', `0 0 ${width} ${height}`);
    }

    function drawConnectionLine() {
      if (!imageContainer || !floatingBox || !svgLine || !svgDot) return;
      if (window.innerWidth <= 1024) return;

      const imageRect = imageContainer.getBoundingClientRect();
      const textRect = floatingBox.getBoundingClientRect();

      const startX = imageRect.left + (imageRect.width / 2);
      const startY = imageRect.top + (imageRect.height / 2);
      const endX = textRect.left;
      const endY = textRect.top + (textRect.height / 2);

      svgDot.setAttribute('cx', startX);
      svgDot.setAttribute('cy', startY);

      const controlPointOffset = 100;
      const pathString = `M ${startX} ${startY} C ${startX + controlPointOffset} ${startY}, ${endX - controlPointOffset} ${endY}, ${endX} ${endY}`;
      svgLine.setAttribute('d', pathString);
    }

    function getScrollProgress() {
      const trackRect = track.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrollMax = trackRect.height - windowHeight;

      if (scrollMax <= 0) return null;

      const scrollPx = -trackRect.top;
      let progress = scrollPx / scrollMax;
      progress = Math.max(0, Math.min(1, progress));

      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      const trackTop = trackRect.top + currentScroll;

      return { progress, scrollMax, trackTop };
    }

    function handleScroll() {
      const trackRect = track.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      if (trackRect.bottom < 0 || trackRect.top > windowHeight) {
        if (svgConnector) svgConnector.style.display = 'none';
        return;
      } else {
        if (svgConnector && window.innerWidth > 1024) svgConnector.style.display = 'block';
      }

      const state = getScrollProgress();
      if (!state) return;

      const totalItems = featuresData.length;
      let newIndex = Math.floor(state.progress * totalItems);
      if (newIndex >= totalItems) newIndex = totalItems - 1;

      updateContent(newIndex);

      const yFloat = (state.progress * 160) - 80;
      floatingBox.style.transform = `translateY(${yFloat}px)`;

      drawConnectionLine();
    }

    function getSnapTarget() {
      const state = getScrollProgress();
      if (!state) return null;
      if (state.progress <= 0.05 || state.progress >= 0.98) return null;

      const totalItems = featuresData.length;
      const exactBoundary = state.progress * totalItems;
      let closestBoundary = scrollDirection === 1 ? Math.ceil(exactBoundary) : Math.floor(exactBoundary);
      closestBoundary = Math.max(0, Math.min(totalItems, closestBoundary));

      const targetProgress = closestBoundary / totalItems;
      const targetY = state.trackTop + targetProgress * state.scrollMax;

      const minY = state.trackTop;
      const maxY = state.trackTop + state.scrollMax;
      if (targetY < minY || targetY > maxY) return null;

      const currentScroll = window.scrollY || document.documentElement.scrollTop;
      return Math.abs(currentScroll - targetY) > 5 ? targetY : null;
    }

    function setSnapping(value) {
      isSnapping = value;
      if (snapSafetyTimeout) clearTimeout(snapSafetyTimeout);
      if (value) {
        snapSafetyTimeout = setTimeout(() => { isSnapping = false; }, 600);
      }
    }

    function executeMobileSnap() {
      const currentScroll = window.scrollY || document.documentElement.scrollTop;

      if (currentScroll !== lastScrollPos) {
        lastScrollPos = currentScroll;
        scrollSnapTimeout = setTimeout(executeMobileSnap, 100);
        return;
      }

      const targetY = getSnapTarget();
      if (targetY !== null) {
        setSnapping(true);
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    }

    function handleScrollEnd() {
      if (window.innerWidth > 1024) return;
      if (isSnapping) return;

      const targetY = getSnapTarget();
      if (targetY !== null) {
        setSnapping(true);
        window.scrollTo({ top: targetY, behavior: 'smooth' });
      }
    }

    function onScroll() {
      const currentScrollY = window.scrollY || document.documentElement.scrollTop;

      if (currentScrollY > lastScrollPos) scrollDirection = 1;
      else if (currentScrollY < lastScrollPos) scrollDirection = -1;
      lastScrollPos = currentScrollY;

      if (!isTicking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          isTicking = false;
        });
        isTicking = true;
      }

      if (!supportsScrollEnd && window.innerWidth <= 1024) {
        if (scrollSnapTimeout) clearTimeout(scrollSnapTimeout);
        if (isSnapping) {
          scrollSnapTimeout = setTimeout(() => { isSnapping = false; }, 150);
        } else {
          scrollSnapTimeout = setTimeout(executeMobileSnap, 150);
        }
      }
    }

    function onResize() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        updateSvgDimensions();
        handleScroll();
        drawConnectionLine();
      }, 150);
    }

    function onTrackerClick(i) {
      const windowScrollY = window.scrollY || document.documentElement.scrollTop;
      const trackTop = track.getBoundingClientRect().top + windowScrollY;
      const targetY = trackTop + (i * window.innerHeight);

      setSnapping(true);
      window.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    const trackerClickHandlers = [];
    trackerItems.forEach((item, i) => {
      const handler = () => onTrackerClick(i);
      trackerClickHandlers.push(handler);
      item.addEventListener('click', handler);
    });

    window.addEventListener('scroll', onScroll, { passive: true });
    if (supportsScrollEnd) {
      window.addEventListener('scrollend', handleScrollEnd, { passive: true });
    }
    window.addEventListener('resize', onResize);

    updateSvgDimensions();
    handleScroll();
    setTimeout(drawConnectionLine, 100);

    initializedSections.set(section, {
      onScroll,
      onResize,
      handleScrollEnd,
      trackerItems,
      trackerClickHandlers,
      timeouts: () => [transitionTimeout, scrollSnapTimeout, snapSafetyTimeout, resizeTimeout],
    });
  }

  function teardownSection(section) {
    const ctx = initializedSections.get(section);
    if (!ctx) return;

    window.removeEventListener('scroll', ctx.onScroll);
    window.removeEventListener('resize', ctx.onResize);
    if (supportsScrollEnd) {
      window.removeEventListener('scrollend', ctx.handleScrollEnd);
    }
    ctx.trackerItems.forEach((item, i) => {
      item.removeEventListener('click', ctx.trackerClickHandlers[i]);
    });
    ctx.timeouts().forEach(t => t && clearTimeout(t));

    initializedSections.delete(section);
  }

  function initAll(root = document) {
    root.querySelectorAll('.js-scrolling-features').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.js-scrolling-features');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.js-scrolling-features');
    if (section) teardownSection(section);
  });
})();